import {useState, useEffect, useMemo} from 'react'
import {DateObject, Calendar} from 'react-multi-date-picker'
import {
  apiDatesToCalendarRanges,
  calendarRangesToApiDates,
} from '../../utils/noticeDateRanges'
import {theme} from '../../theme'
import './styles.css'
import {formatNightShiftDate} from '../../utils/formatNightShiftDate'
import dayjs from '../../utils/localDayjs'

interface InputMultiDateRangeType {
  // Accepte une liste plate de dates (string[]) de l'API
  initialValues?: string[]
  // Retourne une liste plate de dates (string[]) pour l'API
  onChange: (dates: string[]) => void
  isError?: boolean
  isRequired?: boolean
  label?: string
  name?: string
  /** When true, renders each cell as "D-1 → D" to represent night shifts */
  isNight?: boolean
  /**
   * Allow several disjoint ranges (default). Set to `false` to constrain the
   * picker to a single contiguous start→end span (one timeframe only).
   */
  multiple?: boolean
}

const InputMultiDateRangeField = (props: InputMultiDateRangeType) => {
  const {
    onChange,
    initialValues,
    isError,
    isRequired = false,
    label,
    name,
    isNight = false,
    multiple = true,
  } = props
  const [values, setValues] = useState<DateObject[][]>([])

  /**
   * Month the calendar opens on. Defaults (via `undefined`) to today, but when
   * days are pre-selected we open on the earliest one so the selection is
   * visible on mount instead of off-screen. Memoized on `initialValues` so a
   * re-render (e.g. picking a day) keeps the same reference and doesn't snap
   * the view back to the initial month while the user navigates.
   */
  const currentDate = useMemo(() => {
    if (!Array.isArray(initialValues) || initialValues.length === 0) {
      return undefined
    }
    const earliest = [...initialValues].sort()[0]
    const parsed = dayjs(earliest)
    return new DateObject({
      year: parsed.year(),
      month: parsed.month() + 1,
      day: parsed.date(),
    })
  }, [initialValues])

  useEffect(() => {
    if (!Array.isArray(initialValues) || initialValues.length === 0) {
      setValues([])
      return
    }
    // Convertir string[] de l'API en DateObject[][] pour le calendrier
    setValues(apiDatesToCalendarRanges(initialValues))
  }, [initialValues])

  const handleChange = (val: DateObject[][] | DateObject[] | null) => {
    const raw = Array.isArray(val) ? val : []
    // In single-range mode the picker emits a flat DateObject[] (the two
    // endpoints); wrap it as one range so the rest of the pipeline stays on the
    // DateObject[][] shape the conversion utils expect.
    const next: DateObject[][] = multiple
      ? (raw as DateObject[][])
      : [raw as DateObject[]]
    // Convertir DateObject[][] en string[] pour l'API
    const apiDates = calendarRangesToApiDates(next)
    onChange(apiDates)
    setValues(next)
  }

  /**
   * Renders each calendar cell as "D-1 → D" to represent a night shift.
   * The stored value remains D (YYYY-MM-DD) — only the visual label changes.
   */
  const nightMapDays = ({date}: {date: DateObject}) => {
    return {
      children: (
        <span className='night-cell'>
          {formatNightShiftDate(dayjs(date.toDate()), 'minimal')}
        </span>
      ),
    }
  }

  return (
    <div
      className={`singleWrapper${isError ? ' error' : ''}${isNight ? ' night' : ''}`}
      style={{width: '100%'}}>
      {!!label && (
        <label className={'LabelInput'} htmlFor={name}>
          {label}
          {isRequired && <span className='required'>*</span>}
        </label>
      )}
      <div
        className='calendarWrapper'
        style={{
          width: 'fit-content',
          ...(isNight
            ? {backgroundColor: theme.palette.shift.night.light}
            : {}),
        }}>
        <Calendar
          value={multiple ? values : (values[0] ?? [])}
          currentDate={currentDate}
          onChange={val => handleChange(val)}
          numberOfMonths={2}
          range
          multiple={multiple}
          weekStartDayIndex={1}
          weekDays={
            isNight
              ? ['Sa/Di', 'Di/Lu', 'Lu/Ma', 'Ma/Me', 'Me/Je', 'Je/Ve', 'Ve/Sa']
              : undefined
          }
          mapDays={isNight ? nightMapDays : undefined}
        />
      </div>
    </div>
  )
}

export default InputMultiDateRangeField
